import {ErrorInfo, Key, PureComponent, ReactNode} from 'react';

import {reportToBugsnag} from '../../../libs/shared/core/src/services/bugsnag/bugsnag';

/*
 * Props.
 */

interface ErrorReporterBaseProps {
  errorKey?: Key;
  onError?: (error: any) => void;
  renderFallback: (props: {error: any; onRetry: () => void}) => ReactNode;
  children: ReactNode;
}

/*
 * Component.
 */

interface ErrorReporterBaseState {
  prevErrorKey?: Key;
  error?: any;
}

export class ErrorReporterBase extends PureComponent<ErrorReporterBaseProps, ErrorReporterBaseState> {
  constructor(props: ErrorReporterBaseProps) {
    super(props);

    this.state = {
      prevErrorKey: props.errorKey,
    };
  }

  static getDerivedStateFromProps(
    nextProps: Readonly<ErrorReporterBaseProps>,
    state: ErrorReporterBaseState,
  ): Partial<ErrorReporterBaseState> | null {
    if (state.prevErrorKey === nextProps.errorKey) {
      return null;
    }

    return {
      error: undefined,
      prevErrorKey: nextProps.errorKey,
    };
  }

  static getDerivedStateFromError(error: any) {
    return {error};
  }

  componentDidCatch(error: any, errorInfo: ErrorInfo) {
    reportToBugsnag(error, {
      metadata: {
        react: {componentStack: formatComponentStack(errorInfo.componentStack || '')},
        front: {
          didTriggerWhiteScreen: false,
          level: 'render',
        },
      },
    });

    this.props.onError?.(error);
  }

  render() {
    if (this.state.error) {
      return this.props.renderFallback({
        error: this.state.error,
        onRetry: () => this.setState({error: undefined}),
      });
    }

    return this.props.children;
  }
}

/*
 * Helpers.
 */

function formatComponentStack(str: string) {
  const lines = str.split(/\s*\n\s*/g);
  let ret = '';
  for (let line = 0, len = lines.length; line < len; line++) {
    if (lines[line].length) {
      ret += `${ret.length ? '\n' : ''}${lines[line]}`;
    }
  }
  return ret;
}
